import { FC } from 'react';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
// mui
import { Box, Grid } from '@mui/material';
// custom hooks
import { useCatchServerError } from 'hooks/useCatchServerError';
import useToast from 'hooks/useToast';
// api
import {
  useLazyGetFileCabinetSelectionUploadURlQuery,
  usePostFileToAWSMutation,
} from 'redux-store/api/file-cabinet';
// icons
import ImageUploadIcon from 'assets/icons/new/selection/image-upload.svg';
import UploadIcon from 'assets/icons/new/selection/upload.svg';
// utils
import { MAX_FILE_SIZE } from 'utils/constants';
import { ISelectionFormOptions } from '../../../utils/types';
// styles
import { styles } from './styles';

interface IProps {
  optionIndex: number;
  isDescriptionEditDisable?: boolean;
  projectId: number;
}

const SelectionImage: FC<IProps> = ({ optionIndex, projectId, isDescriptionEditDisable }) => {
  const [getUploadUrl] = useLazyGetFileCabinetSelectionUploadURlQuery();
  const [postFileToAWS] = usePostFileToAWSMutation();

  const showToast = useToast();
  const { catchError } = useCatchServerError();

  const form = useFormContext<ISelectionFormOptions>();

  const logo = form.watch(`options.${optionIndex}.logo`);
  const selectionUrl = form.watch(`options.${optionIndex}.url`);

  const updateLogo = (id: string, url: string) => () => {
    form.setValue(`options.${optionIndex}.logo`, { id, url });
  };

  const deleteLogo = () => {
    form.setValue(`options.${optionIndex}.logo`, null);
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    accept: 'image/jpeg, image/png',
    noClick: true,
    noKeyboard: true,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles?.length) {
        if (
          rejectedFiles.find(({ errors }) =>
            errors.find(({ code }) => code === ErrorCode.FileInvalidType)
          )
        ) {
          showToast({ type: 'error', message: 'This file type is not supported.' });
        }
      }

      if (!acceptedFiles?.length) {
        return;
      }

      const file = acceptedFiles[0];

      try {
        await getUploadUrl({
          key: file.name,
          mimeType: file.type,
          isLogo: true,
          ...(!!projectId && { projectId }),
        })
          .unwrap()
          .then(async (res) => {
            const formData = new FormData();
            formData.append('file', file);
            await postFileToAWS({
              URL: res.url,
              file: file,
              fileType: file.type,
            })
              .unwrap()
              .then(updateLogo(String(res.id), res.previewUrl))
              .catch(catchError);
          })
          .catch(catchError);
      } catch (e) {
        showToast({ type: 'error', message: 'Something went wrong!' });
      }
    },
  });

  return (
    <Grid sx={styles.uploadContainer} {...getRootProps()}>
      <Box sx={styles.imageUploadBox}>
        {logo?.url ? (
          <img src={logo.url} alt="img" />
        ) : selectionUrl?.length > 18 ? (
          <img src={selectionUrl} alt="img" />
        ) : (
          <ImageUploadIcon />
        )}
      </Box>
      {!isDescriptionEditDisable && (
        <Grid sx={styles.uploadInput}>
          <label onClick={open} className="add-selection-image">
            <UploadIcon />
            Upload New
          </label>
          {logo?.url && (
            <label onClick={deleteLogo} className="remove-selection-image">
              Delete
            </label>
          )}
          <input type="file" {...getInputProps()} />
        </Grid>
      )}
    </Grid>
  );
};

export default SelectionImage;
